'use client'

import React from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * A term with a definition: dotted underline, explanation on hover or focus.
 *
 * The pattern every trading terminal uses for a label that is not
 * self-evident -- "Unified Account Ratio", "Band" -- and the one place it is
 * styled, so a defined term looks the same in a stats row, a checkbox label
 * and a table header. The underline is the affordance: dotted and quiet,
 * never a link's solid line. The definition is one or two sentences of
 * plain language; a term that needs more than that needs a doc, and the
 * definition should link to it.
 *
 * Keyboard: the term is focusable and the definition opens on focus. Touch:
 * a tap on the term opens it as well.
 */
export default function Term({
	hint,
	children,
	className,
	side = 'top',
	align = 'start'
}: {
	/** The definition. */
	hint: React.ReactNode
	children: React.ReactNode
	className?: string
	side?: 'top' | 'bottom' | 'left' | 'right'
	align?: 'start' | 'center' | 'end'
}) {
	const [open, setOpen] = React.useState(false)
	return (
		<TooltipProvider delayDuration={150}>
			<Tooltip open={open} onOpenChange={setOpen}>
				<TooltipTrigger asChild>
					<span
						tabIndex={0}
						role="button"
						aria-expanded={open}
						// A tap opens the definition on devices with no hover.
						onClick={(e) => {
							e.preventDefault()
							setOpen((v) => !v)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								setOpen((v) => !v)
							}
						}}
						className={cn(
							'cursor-help rounded-sm underline decoration-muted-foreground/50 decoration-dotted decoration-2 underline-offset-4 outline-none focus-visible:decoration-[var(--primary-500)]',
							className
						)}
					>
						{children}
					</span>
				</TooltipTrigger>
				<TooltipContent
					side={side}
					align={align}
					sideOffset={6}
					className="max-w-xs px-3 py-2 text-xs font-normal leading-relaxed text-foreground"
				>
					{hint}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}
